import { FC } from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import Text from "@/components/Text";
import { EventsParticipantResource } from "@/types/resources";
import { categories } from "@/utils/data/productionEvents";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { EventsStatusEnum } from "@/utils/enums";

const Box = styled.div<{ $removedFromMeet?: boolean; $unavailable: boolean }>`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  padding: 12px 16px;
  border: 1px dashed transparent;
  ${({ $removedFromMeet, theme }) =>
    $removedFromMeet &&
    css`
      border-color: ${theme.colors.lightGray};
      background-color: ${theme.colors.white};
    `}
  ${({ $unavailable, theme }) =>
    $unavailable &&
    css`
      border: 1px solid ${theme.colors.error};
      background-color: ${theme.colors.errorLight};
    `}
`;

const Label = styled(Text)`
  margin-bottom: 6px;
`;

const BoxRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CancelButton = styled.button`
  flex-shrink: 0;
  margin-left: 24px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  transition: color ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.white};
    cursor: not-allowed;
    &:hover {
      color: ${({ theme }) => theme.colors.white};
    }
  }
`;

const AddButton = styled(Button)`
  height: 28px;
  padding: 0 15px;
`;

const UnavailableText = styled(Text)`
  margin-top: 6px;
  margin-bottom: -4px;
`;

interface Props {
  removedFromMeet?: boolean;
  unavailable?: boolean;
  participant: EventsParticipantResource;
  eventId: number;
  status: EventsStatusEnum;
}

const ParticipantBlock: FC<Props> = ({
  removedFromMeet = false,
  unavailable = false,
  participant,
  eventId,
  status,
}) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDetach = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to detach this participant from event?`,
        participant.email
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/production/events/${eventId}/participants/${participant.id}/detach`
      );

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Participant was successfully detached");
    } catch (e) {
      alerts.error();
    }
  };

  const onRestore = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to attach this participant to event?`,
        participant.email
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/production/events/${eventId}/participants/${participant.id}/restore`
      );

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Participant was successfully attached");
    } catch (e) {
      alerts.error();
    }
  };

  const renderUserLabel = (category: string) => {
    if (category === "Owner") return "Owner";
    return categories.find(
      (categoryGroup) => category === categoryGroup.category
    )?.label;
  };

  return (
    <div>
      <Box $removedFromMeet={removedFromMeet} $unavailable={unavailable}>
        <BoxRow>
          <div>
            <Label $type="smallHeadline" $color="gray">
              {renderUserLabel(participant.category)}
            </Label>
            <Text $type="button" $color={removedFromMeet ? "gray" : "black"}>
              {participant.email}
            </Text>
          </div>
          {removedFromMeet ? (
            <AddButton
              disabled={status === EventsStatusEnum.SENT}
              small
              color="secondary"
              onClick={() => onRestore()}
            >
              Add
            </AddButton>
          ) : (
            <CancelButton
              disabled={status === EventsStatusEnum.SENT}
              onClick={() => onDetach()}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 3L3 9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 9L3 3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CancelButton>
          )}
        </BoxRow>
      </Box>
      {unavailable && (
        <UnavailableText $type="smallHeadline" $color="error">
          {renderUserLabel(participant.category)} is not available at this time
        </UnavailableText>
      )}
    </div>
  );
};

export default ParticipantBlock;
